import React, { useState } from 'react';
import {
  ALL_CUSTOMIZATION_GROUPS,
  CUSTOMIZATION_LABELS,
  CustomizationGroupId,
  MenuItemCustomizationConfig,
  suggestCustomizationConfig,
} from '../config/drinkCustomizations';

interface MenuCustomizationPickerProps {
  config: MenuItemCustomizationConfig;
  onChange: (config: MenuItemCustomizationConfig) => void;
  category: string;
  name: string;
  compact?: boolean;
}

export default function MenuCustomizationPicker({
  config,
  onChange,
  category,
  name,
  compact = false,
}: MenuCustomizationPickerProps) {
  const [expanded, setExpanded] = useState<Record<string, boolean>>({});

  const isGroupOn = (groupId: string) => {
    const opts = config[groupId as CustomizationGroupId];
    return Array.isArray(opts) && opts.length > 0;
  };

  const toggleGroup = (groupId: CustomizationGroupId, on: boolean) => {
    const group = ALL_CUSTOMIZATION_GROUPS.find((g) => g.id === groupId);
    if (!group) return;
    const next = { ...config };
    if (on) {
      next[groupId] = group.options.map((o) => o.value);
      setExpanded((e) => ({ ...e, [groupId]: true }));
    } else {
      delete next[groupId];
    }
    onChange(next);
  };

  const toggleOption = (groupId: CustomizationGroupId, value: string, checked: boolean) => {
    const current = config[groupId] || [];
    let nextValues: string[];
    if (checked) {
      nextValues = [...current, value];
    } else {
      nextValues = current.filter((v) => v !== value);
    }
    const next = { ...config };
    if (nextValues.length) {
      next[groupId] = nextValues;
    } else {
      delete next[groupId];
    }
    onChange(next);
  };

  const setAllInGroup = (groupId: CustomizationGroupId, selectAll: boolean) => {
    const group = ALL_CUSTOMIZATION_GROUPS.find((g) => g.id === groupId);
    if (!group) return;
    const next = { ...config };
    if (selectAll) {
      next[groupId] = group.options.map((o) => o.value);
    } else {
      delete next[groupId];
    }
    onChange(next);
  };

  const applySuggestion = () => {
    onChange(suggestCustomizationConfig({ category, name }));
  };

  const enabledCount = Object.keys(config).filter((k) => config[k as CustomizationGroupId]?.length).length;

  return (
    <div className={compact ? 'space-y-2' : 'md:col-span-2 space-y-3'}>
      <div className="flex flex-wrap items-center justify-between gap-2">
        <label className="block text-sm font-semibold text-gray-700">
          Customer drink options
        </label>
        <button
          type="button"
          onClick={applySuggestion}
          className="text-xs font-semibold text-amber-700 hover:text-amber-900 underline"
        >
          Suggest from name &amp; category
        </button>
      </div>
      <p className="text-xs text-gray-500">
        Turn on a category, then check only what your store stocks. Customers see those choices on
        the phone app for this item.
      </p>

      <div className="space-y-3">
        {ALL_CUSTOMIZATION_GROUPS.map((group) => {
          const gid = group.id as CustomizationGroupId;
          const on = isGroupOn(gid);
          const selected = new Set(config[gid] || []);
          const isOpen = expanded[gid] ?? on;

          return (
            <div
              key={gid}
              className={`rounded-lg border overflow-hidden ${
                on ? 'border-amber-400 bg-amber-50/50' : 'border-gray-200 bg-white'
              }`}
            >
              <div className="flex items-center gap-2 p-2.5">
                <input
                  type="checkbox"
                  className="mt-0"
                  checked={on}
                  onChange={(e) => toggleGroup(gid, e.target.checked)}
                />
                <button
                  type="button"
                  className="flex-1 text-left text-sm font-semibold text-gray-800"
                  onClick={() => setExpanded((e) => ({ ...e, [gid]: !isOpen }))}
                >
                  {CUSTOMIZATION_LABELS[gid] || group.label}
                  {on && (
                    <span className="ml-2 text-xs font-normal text-gray-500">
                      {selected.size} of {group.options.length} selected
                    </span>
                  )}
                </button>
                {on && (
                  <button
                    type="button"
                    className="text-xs text-amber-800 underline shrink-0"
                    onClick={() => setExpanded((e) => ({ ...e, [gid]: !isOpen }))}
                  >
                    {isOpen ? 'Hide' : 'Edit'}
                  </button>
                )}
              </div>

              {on && isOpen && (
                <div className="px-3 pb-3 pt-0 border-t border-amber-200/80 bg-white/80">
                  <div className="flex gap-3 mb-2 text-xs">
                    <button
                      type="button"
                      className="text-amber-800 font-semibold underline"
                      onClick={() => setAllInGroup(gid, true)}
                    >
                      Select all
                    </button>
                    <button
                      type="button"
                      className="text-gray-600 underline"
                      onClick={() => setAllInGroup(gid, false)}
                    >
                      Clear category
                    </button>
                  </div>
                  <div className="grid grid-cols-1 sm:grid-cols-2 gap-1.5">
                    {group.options.map((option) => (
                      <label
                        key={option.value}
                        className="flex items-center gap-2 text-sm text-gray-700 cursor-pointer py-0.5"
                      >
                        <input
                          type="checkbox"
                          checked={selected.has(option.value)}
                          onChange={(e) => toggleOption(gid, option.value, e.target.checked)}
                        />
                        <span>{option.label}</span>
                      </label>
                    ))}
                  </div>
                </div>
              )}
            </div>
          );
        })}
      </div>

      {enabledCount === 0 && (
        <p className="text-xs text-amber-800">No options selected — customers will only add a note.</p>
      )}
    </div>
  );
}
